import { useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { ApiError, apiFetch } from '@/lib/apiClient'
import type { UserProfile } from '@/types/session'
import type { WorkspacePlanTier } from '../hooks/useWorkspacePlanTier'
import { TEAM_MIN_SEATS } from './billing-constants'
import type { Billing } from './billing-constants'
import { BillingToggle } from './BillingToggle'
import { SeatStepper } from './SeatStepper'
import { PlanCard } from './PlanCard'

const isNativeMobile = Capacitor.isNativePlatform()

export type PlanPickerContext = 'first_login' | 'upgrade'

interface PlanPickerProps {
  user: UserProfile
  authToken: string | null
  onError: (msg: string | null) => void
  /**
   * 'first_login'  — new free signup; Free CTA accepts agreements and lands in app.
   * 'upgrade'      — existing user clicked Upgrade; Free CTA closes the gate.
   */
  context: PlanPickerContext
  /** Called when the user dismisses the gate (upgrade context only). Skips full page reload. */
  onDismiss?: () => void
}

interface AcceptAgreementsResponse {
  success?: boolean
  accessToken?: string
  error?: string
}

interface CheckoutResponse {
  url?: string
  sessionId?: string
}

export function PlanPicker({ authToken, onError, context, onDismiss }: PlanPickerProps) {
  const [submitting, setSubmitting] = useState<WorkspacePlanTier | null>(null)
  const [billing, setBilling] = useState<Billing>('monthly')
  const [teamSeats, setTeamSeats] = useState<number>(TEAM_MIN_SEATS)
  // Set when upgrading an EXISTING workspace to Team trips the exposure gate
  // (TYPE_FLIP_NOT_CONFIRMED) — drives the Confirm dialog. New/first-login
  // workspaces are empty, so they auto-confirm and never set this.
  const [confirmTeamExposure, setConfirmTeamExposure] = useState(false)
  const isUpgradeContext = context === 'upgrade'

  function setStorageToken(token: string | undefined) {
    if (!token) return
    sessionStorage.setItem('xavior_auth_token', token)
    localStorage.setItem('accessToken', token)
  }

  async function acceptAgreements(): Promise<AcceptAgreementsResponse | null> {
    const result = await apiFetch<AcceptAgreementsResponse>(
      'auth/accept-agreements',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        authToken,
      },
    )
    // ADR 010 onboarding state machine: record that the user passed the
    // plan-pick gate. Fire-and-forget — failure here must not block the
    // signup flow (state can be re-advanced later from chat).
    // The refreshed JWT from accept-agreements (when present) carries the
    // updated claims; use it so the advance authenticates as the same user.
    const advanceToken = result?.accessToken ?? authToken
    if (advanceToken) {
      await apiFetch('onboarding/advance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: 'email_pitched' }),
        authToken: advanceToken,
      }).catch(() => {
        // Illegal transition or transient failure — non-fatal, user still proceeds.
      })
    }
    return result
  }

  function handleErr(err: unknown) {
    if (err instanceof ApiError) {
      const payload = err.payload as { error?: string } | undefined
      onError(payload?.error ?? 'Request failed. Please try again.')
    } else {
      onError('Request failed. Please try again.')
    }
  }

  async function startFree() {
    // Upgrade context: user is already on Free + has agreements. CTA is
    // "Stay on Free" — close the gate without a page reload so it doesn't
    // immediately reappear. Strip ?upgrade=1 if present.
    if (isUpgradeContext) {
      if (onDismiss) {
        const url = new URL(window.location.href)
        url.searchParams.delete('upgrade')
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}` || '/')
        onDismiss()
      } else {
        const url = new URL(window.location.href)
        url.searchParams.delete('upgrade')
        window.location.href = `${url.pathname}${url.search}${url.hash}` || '/'
      }
      return
    }

    // First-login context: clicking Start Free implicitly accepts agreements
    // (Pattern A — disclosure shown under the CTA button). Server still
    // records the acceptance via /auth/accept-agreements.
    if (!authToken) {
      onError('Your session expired—please log in again to continue.')
      return
    }
    setSubmitting('free')
    onError(null)
    try {
      const response = await acceptAgreements()
      setStorageToken(response?.accessToken)
      // Hold a deterministic "setting up" loader across the full reload so the
      // gate chain can't flash a blank before session/plan/container state settles
      // (W6). Cleared by AppContent once the app is ready.
      try {
        sessionStorage.setItem('xavior:entering-app', '1')
      } catch {
        /* private mode — proceed without the loader hold */
      }
      window.location.reload()
    } catch (err) {
      handleErr(err)
      setSubmitting(null)
    }
  }

  async function startPaid(plan: 'pro' | 'team', confirmExposure = false) {
    if (!authToken) {
      onError('Your session expired—please log in again to continue.')
      return
    }
    setSubmitting(plan)
    onError(null)
    try {
      const ack = isUpgradeContext ? null : await acceptAgreements()
      setStorageToken(ack?.accessToken)

      if (isNativeMobile) {
        onError('Please complete this purchase on the web. Mobile checkout for this plan is coming soon.')
        setSubmitting(null)
        return
      }

      const body: { plan: string; billing: Billing; quantity?: number; confirmExposure?: boolean } = { plan, billing }
      if (plan === 'team') {
        body.quantity = teamSeats
        // First-login picks Team for a brand-new, empty workspace — nothing to
        // expose, so auto-confirm. Upgrading an EXISTING workspace requires the
        // explicit confirm step (gate → Confirm dialog → retry with true).
        if (!isUpgradeContext || confirmExposure) {
          body.confirmExposure = true
        }
      }

      const response = await apiFetch<CheckoutResponse>(
        'payment/create-checkout',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          authToken: ack?.accessToken ?? authToken,
        },
      )

      if (response?.url) {
        window.location.href = response.url
        return
      }
      if (response?.sessionId) {
        window.location.href = `https://checkout.stripe.com/pay/${response.sessionId}`
        return
      }
      onError('Unable to prepare checkout. Please try again.')
      setSubmitting(null)
    } catch (err) {
      // Upgrading an existing workspace to Team needs a one-time exposure
      // acknowledgement — show a Confirm dialog instead of a dead-end error.
      if (
        err instanceof ApiError &&
        (err.payload as { code?: string } | undefined)?.code === 'TYPE_FLIP_NOT_CONFIRMED'
      ) {
        setConfirmTeamExposure(true)
        setSubmitting(null)
        return
      }
      handleErr(err)
      setSubmitting(null)
    }
  }

  return (
    <div className="w-full max-w-6xl">
      {confirmTeamExposure && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setConfirmTeamExposure(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">Share this workspace with your team?</h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-600">
              Upgrading to Team makes everything currently in this workspace visible to the people you
              invite. Your connected email, calendar, and drive stay private to you.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                onClick={() => setConfirmTeamExposure(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting !== null}
                className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-600 disabled:opacity-60"
                onClick={() => { setConfirmTeamExposure(false); void startPaid('team', true) }}
              >
                {submitting === 'team' ? 'Starting…' : 'Confirm & continue'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mb-8 text-center md:mb-10">
        <div className="mb-4 inline-flex items-center justify-center md:mb-6">
          <img src="/favicon.svg" alt="XAVIOR" className="h-10 w-10 md:h-12 md:w-12" />
        </div>
        <h1 className="text-2xl font-medium leading-tight tracking-tight text-gray-900 md:text-4xl">
          {isUpgradeContext
            ? 'Pick the plan that fits.'
            : 'Free for 14 days. Private when it matters.'}
        </h1>
        {isUpgradeContext ? (
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-gray-600">
            You&rsquo;re on the Free plan today. Upgrade for a dedicated private instance, priority models, and unlimited memory.
          </p>
        ) : (
          <p className="mx-auto mt-4 max-w-[600px] text-base leading-relaxed text-gray-600">
            Xavior gives you the full workspace free for 14 days — modules, Google sync, and memory. Upgrade to keep it, get a private instance, or share a team space.
          </p>
        )}
      </div>

      <BillingToggle billing={billing} onChange={setBilling} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <PlanCard
          orderClass="order-2 md:order-1"
          name="Free"
          tagline="The full product. Every module. Free for 14 days."
          price="$0"
          period="/ month"
          subPrice="No credit card required"
          ctaText={
            submitting === 'free'
              ? 'Starting…'
              : isUpgradeContext
                ? 'Stay on Free'
                : 'Start Free'
          }
          ctaVariant="secondary"
          ctaDisabled={submitting !== null}
          onCta={startFree}
          featuresHeading={isUpgradeContext ? 'Your current plan' : "What's included"}
          features={[
            'Xavior AI Agent',
            'All 10 modules',
            'Gmail, Calendar, Drive & Contacts sync',
            'Persistent memory across every conversation',
            'File and photo uploads',
          ]}
        />

        <PlanCard
          orderClass="order-1 md:order-2"
          featured
          name="Pro"
          tagline="Your own private instance. Nothing shared with anyone."
          price={billing === 'yearly' ? '$25' : '$29'}
          period="/ month"
          subPrice={billing === 'yearly' ? 'Billed $300/year' : '$25/mo billed annually'}
          ctaText={submitting === 'pro' ? 'Starting…' : 'Start Pro'}
          ctaVariant="primary"
          ctaDisabled={submitting !== null}
          onCta={() => startPaid('pro')}
          featuresHeading="Everything in Free, plus"
          features={[
            ['Dedicated private instance', '— your data in its own container, never commingled'],
            'Priority access to the newest frontier models',
            'Unlimited memory and long-context sessions',
            'Priority response times',
            'Email & chat support',
          ]}
        />

        <PlanCard
          orderClass="order-3 md:order-3"
          name="Team"
          tagline="Shared context for firms, offices, and partners."
          price={billing === 'yearly' ? '$39' : '$49'}
          period="/ seat / mo"
          subPrice={
            billing === 'yearly'
              ? `Billed annually · ${teamSeats} seats = $${teamSeats * 39 * 12}/yr`
              : `${teamSeats} seats = $${teamSeats * 49}/mo · min 2 seats`
          }
          ctaText={submitting === 'team' ? 'Starting…' : `Start Team — ${teamSeats} seats`}
          ctaVariant="secondary"
          ctaDisabled={submitting !== null}
          onCta={() => startPaid('team')}
          featuresHeading="Everything in Pro, plus"
          features={[
            ['Shared contacts & CRM', 'across the team'],
            'Shared calendar intelligence & scheduling',
            'Shared Pages & collaborative memory',
            'Multi-seat workspace with invite + member management',
          ]}
          extraSlot={<SeatStepper seats={teamSeats} onChange={setTeamSeats} disabled={submitting !== null} />}
        />
      </div>
    </div>
  )
}
